/**
 * The Ops console's transition contract — one schema map, two consumers.
 *
 * The console renders each action's form from the same zod schema the API's `ZodPipe`
 * runs, so a field the console collects and the server rejects cannot exist. That is the
 * whole reason this lives in `@tard/contracts` rather than in `apps/api`.
 *
 * ## What the payloads are for
 *
 * Every field below exists because a **guard named in `02a-state-machines.md` needs it**.
 * The state machine refuses a transition whose guard is unregistered or false, and the
 * guards are pure predicates over a context — so the evidence a table row demands has to
 * arrive in the request. `scannedBarcodes` is not a nicety, it is what
 * `allParcelBarcodesScanned` reads; `evidenceRef` is what `attemptEvidenceAttached` reads.
 *
 * ## The pilot trigger set is smaller than the machine
 *
 * `SUPPORTED_TRIGGERS` is a **scope decision, not a table**. The machine encodes tables
 * 1a–1e in full; this slice drives the direct-route path and the return flow. Hub
 * composites, address corrections, reschedules and disputes stay in the machine and are
 * refused here with `trigger_not_supported` — a different answer from `transition_refused`,
 * because "we have not built that yet" and "the tables forbid that from this state" are
 * different facts and a console should not conflate them.
 */
import { z } from 'zod';
import { evidenceRefSchema, lossEvidenceRefSchema } from './evidence.js';
import { renderedText } from './rendered-text.js';
/**
 * The triggers this slice serves. Ordered as a Shipment actually moves.
 *
 * Deliberately absent, each for a stated reason:
 * - `leg.transfer.start` / `hub.receive` — hub-routed itineraries; the pilot is same-city
 *   direct (§3: "Same-city orders may bypass hubs").
 * - `address.correction_*` — a cluster-crossing correction has no lawful silent exit
 *   (§5's twin "never" rules, Q16 open); building half of it would be the silent path.
 * - `buyer.reschedule_*` — needs the SLA Clock's pause semantics, which is M4.
 * - `dispute.open_rto_condition` — **still absent, and for a new reason.** Table 1f is
 *   encoded now, but its exit for this dispute resolves a *suspended RTO fee*: 02a says the
 *   ruling either posts it or waives it, so "the dispute branch never strands the fee".
 *   That couples the outcome to Q21's netting policy and deserves its own decision.
 *   Shipping the entry without the exit would strand exactly what the row exists to
 *   protect.
 * - `disposition.adjudicate_abandoned` — a retention policy nobody has ruled (item 26).
 *   `retentionPeriodExhausted` is a hard `false` for the same reason: disposing of
 *   somebody's goods on an invented number is worse than not disposing of them.
 *
 * - `dispute.open` — **still absent, and its reason changed in M3 slice C.** It used to be
 *   "table 1f is not encoded, so `Disputed` has no exit"; 1f is encoded now and that reason
 *   is spent. What blocks it is `disputeWithinWindow`, a hard `false` because the dispute
 *   window is Q19's and nobody has ruled it. Inventing a number would be §10.6's forbidden
 *   move, and a window is not a placeholder like N=3 — it decides whether a Buyer who
 *   complains on day thirty is heard.
 *
 * Table 1f **is** reachable, through `shipment.declare_lost` and `shipment.report_damage`,
 * whose guards are real predicates over evidence the caller supplies. `Damaged` carries the
 * same two-way ruling `Disputed` does, so the ambiguity that made slice C an engine change
 * is exercised either way.
 */
/** Table 1f's own words: how a parcel is lost while the platform holds it. */
export const LOSS_REASONS = ['robbery', 'checkpoint_confiscation', 'missing_at_hub'];
/** What a Buyer or Seller disputes. 1d's `type=` and 1e's `rto_condition`. */
export const DISPUTE_TYPES = ['damage', 'non_delivery', 'item_mismatch', 'rto_condition'];
/**
 * How an adjudication concluded — and the reason code that picks the row.
 *
 * `abandoned_goods` belongs to 1e's retention row, which stays unreachable until item 26
 * rules a retention period; it is listed because the enum is the document's vocabulary,
 * not this slice's subset of it.
 */
export const ADJUDICATION_OUTCOMES = ['ruled', 'return_ruled', 'abandoned_goods'];
export const SUPPORTED_TRIGGERS = [
    'assignment.accept',
    'assignment.bounce',
    'leg.pickup.start',
    'leg.pickup.arrive',
    'leg.pickup.confirm',
    'leg.pickup.fail',
    'pickup.retry',
    'cancel.pre_pickup',
    'leg.delivery.start',
    'leg.delivery.arrive',
    'leg.delivery.confirm',
    'leg.delivery.attempt_fail',
    'leg.delivery.reattempt',
    'buyer.unreachable',
    'buyer.reached',
    'rto.initiate',
    'leg.return.start',
    'leg.return.confirm',
    'leg.return.refuse',
    'hub.receive_return',
    'leg.return.restart',
    'rto.restock_confirm',
    'rto.close',
    // 1f — the parcel is gone, broken, or disputed.
    'shipment.declare_lost',
    'shipment.report_damage',
    'dispute.adjudicate',
];
/**
 * Table 1d's closed enum. **`buyer_refused` is not in it, and that is the point.**
 *
 * A door refusal is all-or-nothing (ADR-0014) and goes straight to `rto.initiate` — it
 * never enters the re-attempt cycle. Putting it here would let a Courier record a refusal
 * as a failed attempt, which burns the attempt budget and leaves the Parcel in a state the
 * return flow never reaches.
 */
export const ATTEMPT_FAILURE_REASONS = [
    'buyer_absent',
    /** Tender below tolerance: the Courier does not hand over. The door rule is binary. */
    'cash_short_refused',
    'address_wrong',
    'access_blocked',
];
/** Table 1b's closed enum. */
export const PICKUP_FAILURE_REASONS = [
    'seller_not_ready',
    'seller_closed',
    'parcel_mismatch',
    'restricted_goods_suspected',
    'seller_refused',
];
/**
 * Table 1e's reasons for entering the return flow.
 *
 * ## Why a door refusal is two values and not one
 *
 * ADR-0014 ruled all-or-nothing at the door for the pilot: a Buyer keeping two of three
 * items is recorded as refusing all three, and both outcomes produce the same
 * `rto.initiated`. The ADR also names the cost — "a Buyer keeping two of three items is
 * everyday Lebanese COD, so this will lose orders a competitor would partly salvage" — and
 * sets a measurable revisit gate on how often it happens.
 *
 * §61-63 of that ADR is the part this enum answers: *"the refusal reason enum needs
 * `partial_refusal` as a distinct value from `full_refusal`, so the revisit gate has data
 * to fire on. **Without that split the gate is unmeasurable and the ruling becomes
 * permanent by accident.**"* One `door_refusal` value made the two indistinguishable in
 * RTO reporting for ever, which is exactly how a pilot ruling turns into a permanent one.
 *
 * The transition, the guard and the evidence requirement are identical for both. The only
 * difference is what the log can later be asked.
 */
export const RTO_REASONS = [
    /** The Buyer took none of it. All-or-nothing (ADR-0014) — arrives here directly, never via 1d. */
    'full_refusal',
    /**
     * The Buyer wanted part of it and the door rule refused the whole. Same transition,
     * different fact — and the fact the revisit gate counts.
     */
    'partial_refusal',
    'buyer_cancel',
    'merchant_recall',
    'buyer_unreachable',
    'address_unresolvable',
];
/**
 * The reason codes the **platform itself** writes onto an Order's own stream.
 *
 * Every other vocabulary in this file is one an operator *chooses* from a form, and
 * `action-coverage.test.ts` has held all of them to copy in three languages since M3. These are
 * the ones nobody chooses: an event builder writes them as a literal when the platform does
 * something of its own accord. They were therefore in no list, and a web gate reads no API
 * source — so **every one of them reached a Merchant's own timeline as a bare Latin
 * identifier**, in Arabic, English and French alike. `order-detail.tsx` renders
 * `t('reason.' + code, code)`, and the fallback is the code.
 *
 * `dispatch_queued` is the **second event on every accepted Order**, and `no_budget` is written
 * for every Order whose service class carries no ruled promise — which, with all of Lebanon in
 * scope, is the common case rather than an edge one (CLAUDE.md section 5, ADR-0081).
 *
 * The list is closed so the next one cannot arrive quietly: each site in `shipments.events.ts`,
 * `orders.events.ts` and `sla.events.ts` writes its literal with `satisfies PlatformReasonCode`,
 * so an unlisted code is a **compile error** at the line that writes it, and
 * `action-coverage.test.ts` reads this array so a listed code with no copy is a failing test.
 * Two statements of one rule, as `viewer.ts` keeps I1.
 *
 * It is deliberately *only* the Order's stream. A Merchant, a principal and a config stream are
 * read by Ops screens that translate their own vocabularies, and nothing renders those through
 * this prefix.
 */
export const PLATFORM_REASON_CODES = [
    /** `order.created` - the Merchant's own act, and one of the two that always had copy. */
    'merchant_created',
    /** `order.accepted` - T0. */
    'platform_accepted',
    /** `shipment.dispatch.queued`, immediately after acceptance. */
    'dispatch_queued',
    /** `leg.<kind>.assigned` - informational, because dispatch is the Shipment's fact (ADR-0030). */
    'leg_assigned',
    /** `leg.<kind>.offer_bounced` - a Courier declined, or the offer timed out. */
    'leg_offer_bounced',
    /** `shipment.route_plan.set` - a human approved a suggestion (ADR-0040). */
    'dispatch_suggestion_approved',
    /** `shipment.label.issued`, first time. The event says *issued*: ink on paper is unobservable. */
    'label_initial',
    /** `shipment.label.issued` again - which is what the on-screen reissue warning is about. */
    'label_reprint',
    /** `sla.not_measured` - the class carries no ruled budget, so no clock was started. */
    'no_budget',
    /** `sla.not_measured` - the route resolved to no service class at all. */
    'no_service_class',
];
/**
 * Every event type that can appear on an Order's own stream, as screens name them
 * (`event.<type>`).
 *
 * One stream per Order carries the whole lifecycle (ADR-0030), and a Merchant's own timeline
 * renders **all** of it - so this is the transition events of `SHIPMENT_MACHINE` plus its
 * `informationalEvents`, in the machine's own order. The web cannot import the kernel, so an API
 * test pins this list to the machine, exactly as `SHIPMENT_STATES` and `COD_STATES` are pinned:
 * an event added to 02a without copy fails a build rather than reaching a Merchant as a raw
 * identifier.
 *
 * **Seventeen of these had no copy in any language until slice 6**, found by looking at an
 * Arabic shot of the Order detail, where `shipment.label.issued` sat in the timeline in Latin
 * letters among Arabic sentences. The informational ones are what the curated copy missed:
 * nothing read them off the machine, and the gate beside this one covers states and reason codes
 * rather than event types.
 */
export const ORDER_STREAM_EVENT_TYPES = [
    'order.created',
    'order.accepted',
    'order.cancelled.pre_acceptance',
    'order.cancelled.pre_pickup',
    'shipment.dispatch.queued',
    'leg.pickup.assigned',
    'leg.pickup.offer_bounced',
    'leg.pickup.started',
    'leg.pickup.arrived',
    'leg.pickup.confirmed',
    'leg.pickup.failed',
    'shipment.pickup.retry_scheduled',
    'leg.transfer.started',
    'hub.received',
    'leg.delivery.started',
    'leg.delivery.arrived',
    'leg.delivery.confirmed',
    'leg.delivery.attempt_failed',
    'leg.delivery.reattempt_scheduled',
    'shipment.buyer_unreachable',
    'shipment.address_correction.requested',
    'shipment.reschedule.requested',
    'shipment.buyer_reached',
    'rto.initiated',
    'shipment.address_correction.resolved',
    'order.service_class.changed',
    'shipment.reschedule.window_opened',
    'dispute.opened',
    'leg.return.started',
    'leg.return.confirmed',
    'leg.return.refused',
    'dispute.adjudicated',
    'rto.restocking_confirmed',
    'rto.closed',
    'shipment.lost',
    'shipment.damaged',
    'leg.delivery.assigned',
    'leg.delivery.offer_bounced',
    'leg.transfer.assigned',
    'leg.transfer.offer_bounced',
    'leg.return.assigned',
    'leg.return.offer_bounced',
    'shipment.route_plan.set',
    'shipment.label.issued',
    'ops.custody_conflict',
    'sla.not_measured',
];
/** Whether a stored string is one of `RTO_REASONS` — a projection column is only text. */
export const isRtoReason = (value) => typeof value === 'string' && RTO_REASONS.includes(value);
/** Table 1e: the Seller would not take the Parcel back, or was not there to. */
export const RETURN_REFUSAL_REASONS = [
    'seller_refused',
    'seller_closed',
    'seller_unavailable',
];
/**
 * Why Ops cancels a Shipment before pickup — the codes the console offers for `cancel.pre_pickup`.
 *
 * The contract accepts any reference (`reasonCode: ref(64)`), because a Merchant's own cancel sends
 * `merchant_cancelled`; but the console offers a closed list, for the reason every reason list here
 * is closed: the code lands in an append-only event and in the `order.cancelled.pre_pickup` webhook
 * an integrator reads, where free text could never be corrected or counted. Until this list existed
 * the console sent no `reasonCode` at all, and every Ops cancel before pickup was refused.
 */
export const PRE_PICKUP_CANCEL_REASONS = [
    'merchant_cancelled',
    'buyer_cancel',
    'seller_closed',
    'restricted_goods_suspected',
    'address_unresolvable',
];
/**
 * Every state a Shipment can be in, as screens name them (`orderStatus.<state>`). The machine that
 * owns them is the kernel's `SHIPMENT_MACHINE`, which the web cannot import; an API test pins this
 * list to it, so a state added there without copy here fails a build rather than reaching a screen
 * as a raw identifier.
 */
export const SHIPMENT_STATES = [
    'Draft',
    'Accepted',
    'CancelledPreAcceptance',
    'CancelledPrePickup',
    'AwaitingAssignment',
    'Assigned',
    'EnRouteToPickup',
    'AtPickup',
    'PickedUp',
    'PickupFailed',
    'InTransitToHub',
    'AtHub',
    'OutForDelivery',
    'AtDropoff',
    'Delivered',
    'AttemptFailed',
    'BuyerUnreachable',
    'AddressCorrectionPending',
    'RescheduledByBuyer',
    'Disputed',
    'RtoInitiated',
    'RtoInTransit',
    'ReturnRefused',
    'HeldAtHubPendingDisposition',
    'ReturnedToSeller',
    'RestockingConfirmed',
    'ClosedRto',
    'Adjudicated',
    'Lost',
    'Damaged',
];
/**
 * An opaque reference to something that is **not** an artifact.
 *
 * Deliberately unchanged by M7 slice D, and the list of what still uses it is the point:
 * `contactRef` is a masked-relay session id, `sellerScanRef` is a barcode scan, `rulingRef`
 * is an Ops document, `availabilityRef` is a phone call. None of them addresses bytes, so
 * forcing a content hash on them would be inventing a policy nobody ruled (§10.6). "Make
 * `evidenceRef` a hash" reads like one change and is five — `custody.ts`'s field of the
 * same name is a **cash recount**.
 */
const ref = (max = 200) => z.string().trim().min(1).max(max);
/**
 * Proof of delivery, where only one of the three kinds addresses bytes.
 *
 * A discriminated union rather than an enum beside a loose string, because the rule *is*
 * conditional: a photographed PoD is a commitment to an artifact and must carry its digest;
 * a signature or a door code is not and cannot. Flattening the two would either force a
 * hash on a code that has none, or accept prose for a photograph — the second is where
 * this platform already was.
 */
const podSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('photo'), ref: evidenceRefSchema }),
    z.object({ kind: z.literal('signature'), ref: ref() }),
    z.object({ kind: z.literal('code'), ref: ref() }),
]);
const barcodes = z.array(z.string().trim().min(1).max(64)).min(1).max(50);
/** A geo stamp. Bounds are the planet's; the operating-area check is the API's. */
export const geoStampSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
/**
 * One currency's worth of cash counted at the door.
 *
 * Minor units as a **string** (I2). §6: "Reconciliation must handle a courier collecting
 * mixed currency against a single order" — so this is an array element, never a scalar.
 */
export const tenderSchema = z.object({
    currency: z.enum(['USD', 'LBP', 'SYP']),
    amountMinor: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
});
/**
 * Who really performed the action, when Ops records it for them.
 *
 * The engine allows only `opsUser` to set `onBehalfOf`, and checks the *delegated* role
 * against the table row's `actors`. The event's actor stays the Ops user who typed it —
 * so the log answers both "who did this" and "who keyed it in", which is exactly what an
 * adjudicator reading the trail needs (I4).
 */
export const performedBySchema = z.enum(['courier', 'buyer', 'merchantUser', 'system']);
const empty = z.object({}).strict();
/**
 * Per-trigger payloads. A discriminated union, so the compiler and the parser agree on
 * which fields a given action carries and the console can switch on one value.
 */
export const applyShipmentSchema = z.discriminatedUnion('trigger', [
    z.object({
        trigger: z.literal('assignment.accept'),
        performedBy: performedBySchema.optional(),
        // `courierInPickupZonePool` + the leg stamp both need it.
        payload: z.object({ courierId: ref(64) }),
    }),
    z.object({
        trigger: z.literal('assignment.bounce'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ reasonCode: z.enum(['rejected', 'timeout', 'ops_reassigned']) }),
    }),
    z.object({
        trigger: z.literal('leg.pickup.start'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ geo: geoStampSchema.optional() }),
    }),
    z.object({
        trigger: z.literal('leg.pickup.arrive'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ geo: geoStampSchema.optional() }),
    }),
    z.object({
        trigger: z.literal('leg.pickup.confirm'),
        performedBy: performedBySchema.optional(),
        payload: z.object({
            /** Scan-per-parcel handover (table 1b). The console renders one checkbox per Parcel. */
            scannedBarcodes: barcodes,
            /**
             * Intake capture — item 9's orphan-parcel defence.
             *
             * §4 strips every other identifying mark from a parcel by design, so when a label is
             * destroyed this capture is the *whole* recovery path (exposure row 1). It stays
             * optional because whether intake capture is mandatory is Q19's ruling, not this
             * slice's — but what it accepts is now a commitment to bytes rather than any string.
             */
            intakeRef: evidenceRefSchema.optional(),
            geo: geoStampSchema.optional(),
        }),
    }),
    z.object({
        trigger: z.literal('leg.pickup.fail'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ reasonCode: z.enum(PICKUP_FAILURE_REASONS), geo: geoStampSchema.optional() }),
    }),
    z.object({
        trigger: z.literal('pickup.retry'),
        performedBy: performedBySchema.optional(),
        payload: empty,
    }),
    z.object({
        trigger: z.literal('cancel.pre_pickup'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ reasonCode: ref(64) }),
    }),
    z.object({
        trigger: z.literal('leg.delivery.start'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ geo: geoStampSchema.optional() }),
    }),
    z.object({
        trigger: z.literal('leg.delivery.arrive'),
        performedBy: performedBySchema.optional(),
        // `geoStampNearDropoffPin` checks this against the authoritative pin (§8).
        payload: z.object({ geo: geoStampSchema }),
    }),
    z.object({
        trigger: z.literal('leg.delivery.confirm'),
        performedBy: performedBySchema.optional(),
        payload: z.object({
            pod: podSchema,
            /** Split tender, per currency (§6). Valued against the Order's pinned FX snapshot. */
            tenders: z.array(tenderSchema).min(1).max(3),
            changeGiven: z.array(tenderSchema).max(3).optional(),
            geo: geoStampSchema.optional(),
        }),
    }),
    z.object({
        trigger: z.literal('leg.delivery.attempt_fail'),
        performedBy: performedBySchema.optional(),
        payload: z.object({
            reasonCode: z.enum(ATTEMPT_FAILURE_REASONS),
            evidenceRef: evidenceRefSchema,
            geo: geoStampSchema.optional(),
        }),
    }),
    z.object({
        trigger: z.literal('leg.delivery.reattempt'),
        performedBy: performedBySchema.optional(),
        payload: empty,
    }),
    z.object({
        trigger: z.literal('buyer.unreachable'),
        performedBy: performedBySchema.optional(),
        // §5: a pause needs an actor and a note. Nothing pauses implicitly.
        payload: z.object({ note: z.string().trim().min(1).max(500) }),
    }),
    z.object({
        trigger: z.literal('buyer.reached'),
        performedBy: performedBySchema.optional(),
        /** Through the masking relay — the contact is evidenced, never a number (§4). */
        payload: z.object({ contactRef: ref() }),
    }),
    z.object({
        trigger: z.literal('rto.initiate'),
        performedBy: performedBySchema.optional(),
        payload: z.object({
            reasonCode: z.enum(RTO_REASONS),
            evidenceRef: evidenceRefSchema.optional(),
        }),
    }),
    // ---- 1f ------------------------------------------------------------------
    z.object({
        trigger: z.literal('shipment.declare_lost'),
        performedBy: performedBySchema.optional(),
        /**
         * `lossEvidenced` reads both. The reason is 02a's closed enum; the reference is *that*
         * evidence exists, not what it is — item 28 leaves the required evidence open, and a
         * policy nobody owns should not be enforced as though somebody did.
         */
        payload: z.object({
            reasonCode: z.enum(LOSS_REASONS),
            evidenceRef: lossEvidenceRefSchema,
        }),
    }),
    z.object({
        trigger: z.literal('shipment.report_damage'),
        performedBy: performedBySchema.optional(),
        // `damageEvidenced` reads the photos. Who reported them is on the envelope already —
        // 02a admits a Courier or Ops, and the engine records whichever acted.
        payload: z.object({ evidenceRef: evidenceRefSchema }),
    }),
    z.object({
        trigger: z.literal('dispute.adjudicate'),
        performedBy: performedBySchema.optional(),
        /**
         * `outcome` **is** the transition's reason code — it is what tells
         * `Damaged → Adjudicated` from `Damaged → RtoInitiated`, what the envelope carries, and
         * what `deriveState` reads back to fold the same way the engine decided. One field,
         * three jobs, and before slice C the engine had no way to receive it.
         */
        payload: z.object({ outcome: z.enum(ADJUDICATION_OUTCOMES), rulingRef: ref() }),
    }),
    z.object({
        trigger: z.literal('leg.return.start'),
        performedBy: performedBySchema.optional(),
        // `parcelCustodyConfirmedWithCourier`: the Parcel is where the scans say it is.
        payload: z.object({ scannedBarcodes: barcodes }),
    }),
    z.object({
        trigger: z.literal('leg.return.confirm'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ conditionPhotosRef: evidenceRefSchema, sellerScanRef: ref() }),
    }),
    z.object({
        trigger: z.literal('leg.return.refuse'),
        performedBy: performedBySchema.optional(),
        payload: z.object({
            reasonCode: z.enum(RETURN_REFUSAL_REASONS),
            evidenceRef: evidenceRefSchema,
        }),
    }),
    z.object({
        trigger: z.literal('hub.receive_return'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ hubId: ref(64), scannedBarcodes: barcodes }),
    }),
    z.object({
        trigger: z.literal('leg.return.restart'),
        performedBy: performedBySchema.optional(),
        payload: z.object({ availabilityRef: ref() }),
    }),
    z.object({
        trigger: z.literal('rto.restock_confirm'),
        performedBy: performedBySchema.optional(),
        /** Table 1e records whether the Seller confirmed or a timeout auto-confirmed. */
        payload: z.object({ mode: z.enum(['manual', 'timeout']) }),
    }),
    z.object({
        trigger: z.literal('rto.close'),
        performedBy: performedBySchema.optional(),
        payload: empty,
    }),
]);
const ARTIFACT_REF = 'an evd1_<64 hex> or evdm_<26> reference (evidenceRefSchema): word characters only, never phone-shaped';
/**
 * ADR-0072, for the Ops console and the Courier's device alike. Only `cancel.pre_pickup`'s reason
 * code is free text that is shown (every other `reasonCode` is an enum), on both timelines; the rest
 * of a payload lands in the transition event, which no view renders.
 */
export const applyShipmentRendered = renderedText(applyShipmentSchema, {
    // A code, so a reference rather than prose (see cancelOrderRendered).
    refs: ['payload.reasonCode'],
    exempt: {
        'payload.courierId': "a Courier's public id: assignment.accept's guard refuses one naming no active Courier before any write",
        'payload.scannedBarcodes': "each must be one of the Shipment's own Parcel barcodes: coversEveryParcel refuses an extra before any write",
        'payload.intakeRef': ARTIFACT_REF,
        'payload.conditionPhotosRef': ARTIFACT_REF,
        'payload.evidenceRef': 'an evd1_/evdm_ reference except on shipment.declare_lost, whose free loss reference is stored only in ' +
            'the shipment.lost event payload; the evidence list shows a reference only when it has the evd shape',
        'payload.pod.ref': 'an evd1_/evdm_ reference for a photo; a signature or code reference is stored only in the ' +
            'leg.delivery.confirmed event payload, and no view renders it',
        'payload.tenders.*.amountMinor': 'minor units, digits only: stored in the delivery and cod.collected events and posted to the ledger as ' +
            'an integer; no view shows the string as sent',
        'payload.changeGiven.*.amountMinor': 'minor units, digits only: stored in the delivery and cod.collected events and posted to the ledger as ' +
            'an integer; no view shows the string as sent',
        'payload.note': 'stored only in the shipment.buyer_unreachable and sla.paused event payloads; no view renders it',
        'payload.contactRef': 'stored only in the shipment.buyer_reached event payload; no view renders it',
        'payload.rulingRef': 'stored only in the dispute.adjudicated event payload; no view renders it',
        'payload.sellerScanRef': 'stored only in the leg.return.confirmed event payload; no view renders it',
        'payload.availabilityRef': 'stored only in the leg.return.started event payload; no view renders it',
        'payload.hubId': 'stored only in the hub.received event payload and shipments.custodian_id; no view renders either',
    },
});
/** Assigning a Leg that is not the pickup — the return Leg, at pilot. */
export const assignLegSchema = z.object({ courierId: z.string().trim().min(1).max(64) });
export const assignLegRendered = renderedText(assignLegSchema, {
    exempt: {
        courierId: "a Courier's public id: assignReturnLeg refuses one naming no Courier (invalid_reference) before any write",
    },
});
export const opsBoardSchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/** The `policy_config` keys this slice's guards actually read. */
export const POLICY_KEYS = {
    deliveryAttemptsMax: 'delivery_attempts_max',
    pickupRetriesMax: 'pickup_retries_max',
    codTenderTolerance: 'cod_tender_tolerance',
    rtoFeeTerms: 'rto_fee_terms',
    /** How near the pin a Courier must be to arrive at the door (P2-8a): a constant until 2026-10-02. */
    dropoffProximity: 'dropoff_proximity',
    /** How far apart delivery attempts must be to count towards "unreachable" (P2-8c). */
    deliveryAttemptSpacing: 'delivery_attempt_spacing',
};
//# sourceMappingURL=shipments.js.map